import React, { useRef } from 'react';
import { Field, Form, ProjectTagMultiSelect } from '~/components';
import { useWorkspace } from '~/contexts';
import { useFeatures } from '~/hooks';
import SettingsFormSection from '../../SettingsFormSection';

export default function BasicInformation({ project, formik }) {
  const firstFieldRef = useRef();
  const features = useFeatures();
  const { workspace } = useWorkspace();

  const { client } = formik.values;
  const locked = project.hasInvoices || project.hasCreditNotes;

  const handleClientChange = ({ target: { value } }) => {
    const values = { ...formik.values, client: value };
    if (value && value.isInternal) {
      values.isBillable = false;
      values.billingTypeId = 'non_billable';
      values.enableClientApprovals = false;
      values.salesRepresentative = null;
    }

    if (!value || value.id !== client?.id) {
      values.qboProjectId = null;
    }

    if (client.id !== value.id) {
      values.purchaseOrder = null;
      values.projectGroup = null;
    }

    formik.setValues(values);
  };

  const handleBusinessUnitChange = (event) => {
    const value = event.target.value;
    const values = {
      ...formik.values,
      businessUnit: value,
      accountingProvider: value?.accountingProvider ?? workspace.accountingProvider ?? null,
      accountingConnectionId: value?.accountingConnectionId ?? workspace.accountingConnectionId ?? null,
    };

    values.qboProjectId =
      values.accountingProvider === 'qbo'
        ? (project.qboProjects?.find((m) => m.qboConnectionId === values.accountingConnectionId)?.qboProjectId ?? null)
        : null;

    if (!value && values.invoiceDetailsSource === 'business_unit') values.invoiceDetailsSource = 'client';

    formik.setValues(values);
  };

  return (
    <SettingsFormSection title="Basic Information">
      <Form.Control>
        <Field.ClientSelect
          name="client"
          placeholder="Client"
          initialValue={project.client}
          allowNew
          disabled={locked || project.hasOpportunities}
          clearable={false}
          onChange={handleClientChange}
        />
      </Form.Control>
      <Form.Control>
        <Field.Text ref={firstFieldRef} name="name" placeholder="Project Name" maxLength={255} />
      </Form.Control>
      <Form.Control help="The project identifier used in URLs. Must be unique for the selected client. Changing this will break existing links to the project.">
        <Field.Text name="key" placeholder="Project URL ID" maxLength={255} />
      </Form.Control>
      {features.practices && (
        <Form.Control>
          <Field.PracticeSelect name="practice" placeholder="Practice" allowNew />
        </Form.Control>
      )}
      {features.businessUnits && (
        <Form.Control>
          <Field.BusinessUnitSelect
            name="businessUnit"
            placeholder="Business Unit"
            onChange={handleBusinessUnitChange}
          />
        </Form.Control>
      )}
      {!client.isInternal && (
        <Form.Control>
          <Field.MemberSelect
            name="salesRepresentative"
            placeholder="Sales Representative"
            initialValue={project.salesRepresentative}
          />
        </Form.Control>
      )}
      <Form.Control>
        <Field.ProjectStatusSelect name="statusId" placeholder="Project Status" />
        {formik.values.statusId === 'completed' && <Field.DayPicker name="completedOn" placeholder="Completed On" />}
      </Form.Control>
      <Form.Control>
        <Field.ProjectTypeSelect
          name="projectType"
          placeholder="Project Type"
          initialValue={project.projectType}
          allowNew
        />
      </Form.Control>
      <Form.Control>
        <Field.DayPicker name="start" placeholder="Start Date" />
        <Field.DayPicker name="end" placeholder="End Date" />
      </Form.Control>
      <Form.Control>
        <Field.Text name="code" placeholder="Project Code" maxLength={255} />
      </Form.Control>
      <Form.Control>
        <Field.Control>
          <ProjectTagMultiSelect
            name="tags"
            placeholder="Tags"
            value={formik.values.tags}
            allowNew
            onChange={({ target: { value } }) => formik.setFieldValue('tags', value)}
          />
        </Field.Control>
      </Form.Control>
      {features.projectGroups && (
        <Form.Control>
          <Field.ProjectGroupSelect
            name="projectGroup"
            placeholder="Project Group"
            initialValue={project.projectGroup}
            clientId={formik.values.client?.id}
            allowNew
          />
        </Form.Control>
      )}
      <Form.Control>
        <Field.TextArea name="description" placeholder="Notes" maxLength={5000} />
      </Form.Control>
    </SettingsFormSection>
  );
}
